import { NavLink, useNavigate, useLocation, useParams } from 'react-router-dom';
import { LayoutDashboard, Radio, Building2, Settings, Send, Rss, Sun, Moon, KeyRound, Brain, BarChart3, ChevronDown, ChevronUp, Users, Image as ImageIcon, MessageCircle, LogOut, BookOpen, Loader2, Megaphone, Globe, Crown, X } from 'lucide-react';
import { useTheme } from '../ThemeContext';
import { useT, useDirectionName } from '../i18n';
import api, { cachedGet, settings as settingsApi, assistant as assistantApi, getToken, clearToken } from '../api';
import { useEffect, useState } from 'react';
import { DIRECTION_ICONS, getDirectionColor } from './shared';
import LanguageToggle from './LanguageToggle';
import NotificationCenter from './NotificationCenter';
import ModeSwitcher from './ModeSwitcher';
import FirstDayChecklist from './FirstDayChecklist';
import { useCurrentAssistantId, useAssistant } from '../hooks/useAssistant';
import { VoiceDock } from '../voice/VoicePanel';

// 家庭模式隐藏的工作专属导航 (生活模式只看: 仪表盘/助理/对话/compose/stream/设置/用量)
const FAMILY_HIDDEN = ['/providers', '/leads', '/campaigns', '/deployment', '/knowledge', '/image-studio'];

// 家庭模式: dashboard 标签改为"今日信息/Today" (内容为今日卡, "仪表盘"名不副实)
const navGroups = (assistantId, t, { hasDeployment = false, mode = 'business', plan = 'business' } = {}) => [
  {
    label: t('nav_groups.information'),
    items: [
      { to: `/assistant/${assistantId}/dashboard`, icon: LayoutDashboard, label: mode === 'family' ? t('nav.today') : t('nav.dashboard') },
      { to: `/assistant/${assistantId}/providers`, icon: Building2, label: t('nav.providers') },
      { to: `/assistant/${assistantId}/leads`, icon: Radio, label: t('nav.demand_radar') },
    ],
  },
  {
    label: t('nav_groups.assistant'),
    items: [
      { to: '/assistants', icon: Brain, label: t('nav.my_assistants') },
      { to: `/assistant/${assistantId}/conversations`, icon: MessageCircle, label: t('nav.conversations') },
      { to: `/assistant/${assistantId}/compose`, icon: Send, label: t('nav.compose') },
      { to: `/assistant/${assistantId}/stream`, icon: Rss, label: t('nav.stream') },
      { to: `/assistant/${assistantId}/campaigns`, icon: Megaphone, label: t('nav.campaigns') },
      { to: '/image-studio', icon: ImageIcon, label: t('nav.image_studio') },
      ...(hasDeployment ? [{ to: `/assistant/${assistantId}/deployment`, icon: Globe, label: t('nav.deployment') }] : []),
      { to: `/assistant/${assistantId}/knowledge`, icon: BookOpen, label: t('nav.knowledge_base') },
      { to: `/assistant/${assistantId}/settings`, icon: Settings, label: t('nav.settings') },
    ],
  },
  {
    label: t('nav_groups.system'),
    items: [
      { to: '/usage', icon: BarChart3, label: t('nav.usage') },
      { to: '/change-password', icon: KeyRound, label: t('nav.change_password') },
    ],
  },
];

/* ─── 试用期"选择方案"置顶 pill (W15) — 导航第一位, 高亮常驻, 转正后消失 ─── */
function TrialPlanPill({ trialEndsAt }) {
  const t = useT();
  const dateLabel = trialEndsAt ? String(trialEndsAt).slice(0, 10) : null;
  return (
    <NavLink
      to="/usage#plans"
      className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors"
      style={({ isActive }) => ({
        backgroundColor: isActive ? 'var(--brand-hover)' : 'var(--brand)',
        color: '#fff',
        boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
      })}
    >
      <Crown size={17} />
      <span className="flex-1 text-left">{t('nav.choose_plan')}</span>
      {dateLabel && (
        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.22)' }}>
          {t('sidebar_extra.trial_ends', { date: dateLabel })}
        </span>
      )}
    </NavLink>
  );
}

/* Direction emoji map — imported from ./shared */

function NavGroup({ label, items }) {
  return (
    <>
      <p
        className="px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-wider"
        style={{ color: 'var(--text-muted)' }}
      >
        {label}
      </p>
      {items.map(({ to, icon: Icon, label: itemLabel, badge }) => (
        <NavLink
          key={to}
          to={to}
          end={to === '/dashboard'}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive ? 'text-white' : 'hover:bg-[var(--bg-hover)]'
            }`
          }
          style={({ isActive }) =>
            isActive
              ? { backgroundColor: 'var(--brand)', color: '#fff' }
              : { color: 'var(--text-secondary)' }
          }
        >
          <Icon size={17} />
          <span className="flex-1">{itemLabel}</span>
          {badge && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full"
              style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
              {badge}
            </span>
          )}
        </NavLink>
      ))}
    </>
  );
}

/* ─── Assistant Switcher Dropdown ─── */
function AvatarBadge({ assistant, size = 'md' }) {
  const Icon = DIRECTION_ICONS[assistant?.direction] || DIRECTION_ICONS.default;
  const dirColor = getDirectionColor(assistant?.direction);
  const thumb = assistant?.avatar_thumb;
  const [imgOk, setImgOk] = useState(true);
  useEffect(() => { setImgOk(true); }, [thumb]);
  const dims = size === 'sm'
    ? { box: 30, badge: 15, badgeIcon: 9, gradIcon: 15 }
    : { box: 40, badge: 18, badgeIcon: 11, gradIcon: 20 };
  return (
    <div className="av-badge" style={{ width: dims.box, height: dims.box }}>
      <div className="av-circle" style={{ width: dims.box, height: dims.box }}>
        {thumb && imgOk ? (
          <img src={thumb} alt="" className="av-img" onError={() => setImgOk(false)} />
        ) : (
          <div className="av-grad" style={{ background: `linear-gradient(135deg, ${dirColor}, var(--brand-hover))` }}>
            <Icon size={dims.gradIcon} color="#fff" strokeWidth={2.5} />
          </div>
        )}
      </div>
      <span className="av-dir-badge" style={{ width: dims.badge, height: dims.badge }}>
        <Icon size={dims.badgeIcon} style={{ color: dirColor }} strokeWidth={2.5} />
      </span>
    </div>
  );
}

function AssistantSwitcher() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentId, assistants: localAssistants, setCurrentId } = useCurrentAssistantId();
  const { assistants: ctxAssistants, setAssistant } = useAssistant();
  const [expanded, setExpanded] = useState(false);
  const t = useT();
  const dirName = useDirectionName();

  // Use context's assistants if available (populated by MyAssistants on create/delete)
  const assistants = ctxAssistants.length > 0 ? ctxAssistants : localAssistants;

  const currentPage = (() => {
    const segs = location.pathname.split('/');
    const last = segs[segs.length - 1];
    return ['dashboard','leads','providers','compose','stream','settings','conversations','knowledge','replies','posts','usage','image-studio','campaigns','deployment'].includes(last) ? last : 'dashboard';
  })();

  const current = assistants.find(a => a.id === currentId);

  // Assistant switcher dropdown links use slug
  const switchToSlug = (a) => a.public_id || String(a.id);

  const handleSwitchAssistant = (assistant) => {
    if (!assistant?.id) return;
    const previous = localStorage.getItem('active_assistant_id');
    setCurrentId(assistant.id);
    setAssistant(assistant);
    setExpanded(false);
    navigate(`/assistant/${switchToSlug(assistant)}/${currentPage}`);
    if (previous !== String(assistant.id)) {
      window.dispatchEvent(new Event('assistant:changed'));
    }
  };


  if (assistants.length === 0) {
    return (
      <div className="px-3 pt-2 pb-1">
        <div className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm animate-pulse" style={{ color: 'var(--text-muted)' }}>
          <Loader2 size={16} className="animate-spin" />
          <span className="flex-1 text-left">{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 pt-2 pb-1">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors"
        style={{
          backgroundColor: expanded ? 'var(--bg-hover)' : 'transparent',
          color: 'var(--text-secondary)',
        }}
        title={t('sidebar_extra.switch_assistant')}
      >
        <AvatarBadge assistant={current} />
        <div className="flex-1 min-w-0 text-left">
          <span className="block truncate av-name" style={{ color: 'var(--text-primary)' }}>
            {current?.name || '—'}
          </span>
          <span className="block truncate av-dir" style={{ color: 'var(--text-secondary)' }}>
            <span className="av-dir-dot" style={{ backgroundColor: getDirectionColor(current?.direction) }} />
            {dirName(current?.direction, current?.direction_name)}
          </span>
        </div>
        {expanded ? <ChevronUp size={14} className="shrink-0" /> : <ChevronDown size={14} className="shrink-0" />}
      </button>

      {expanded && (
        <div className="mt-1 space-y-0.5">
          {assistants.map(a => (
            <button
              key={a.id}
              className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs transition-colors ${
                a.id === currentId ? 'font-semibold' : ''
              }`}
              onClick={() => handleSwitchAssistant(a)}
              style={{
                backgroundColor: a.id === currentId ? 'var(--brand)' : 'transparent',
                color: a.id === currentId ? '#fff' : 'var(--text-secondary)',
                cursor: 'pointer',
              }}
            >
              <AvatarBadge assistant={a} size="sm" />
              <div className="flex-1 min-w-0 text-left">
                <span className="block truncate av-dd-name" style={{ color: a.id === currentId ? '#fff' : 'var(--text-primary)' }}>
                  {a.name}
                </span>
                <span className="block truncate av-dd-dir" style={{ color: a.id === currentId ? 'rgba(255,255,255,0.85)' : 'var(--text-secondary)' }}>
                  {dirName(a.direction, a.direction_name)}
                </span>
              </div>
              {a.status !== 'active' && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: 'var(--bg-hover)', color: 'var(--text-muted)' }}>
                  {a.status === 'archived' ? 'Archived' : a.status}
                </span>
              )}
            </button>
          ))}
          <button
            onClick={() => navigate('/assistants')}
            className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs transition-colors mt-1"
            style={{ color: 'var(--brand)' }}
          >
            <Users size={12} />
            {t('nav.my_assistants')}
          </button>
        </div>
      )}
    </div>
  );
}

export default function Sidebar({ onClose, open = false }) {
  const { theme, toggleTheme } = useTheme();
  const t = useT();
  const { currentId, assistants: localAssistants, setCurrentId } = useCurrentAssistantId();
  const { assistants: ctxAssistants } = useAssistant();
  const [agentName, setAgentName] = useState(t('sidebar.default_agent_name'));
  const assistants = ctxAssistants.length > 0 ? ctxAssistants : localAssistants;
  const current = assistants.find(a => a.id === currentId);
  const currentPubId = current?.public_id || String(currentId);
  const assistantId = currentPubId;
  // 助理外派 nav item: only when the CURRENT assistant holds admin-issued keys.
  const [extKeyCount, setExtKeyCount] = useState(0);
  const [mode, setMode] = useState('business');
  const [plan, setPlan] = useState('business');
  const [trialEndsAt, setTrialEndsAt] = useState(null);
  useEffect(() => {
    cachedGet('/auth/me').then(r => {
      setMode(r.data?.user?.mode || 'business');
      setPlan(r.data?.user?.plan || 'business');
      setTrialEndsAt(r.data?.user?.trial_ends_at || null);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!currentId) { setExtKeyCount(0); return; }
    api.get(`/external-chat/keys?assistant_id=${currentId}`)
      .then(res => setExtKeyCount((res.data?.keys || []).length))
      .catch(() => setExtKeyCount(0));
  }, [currentId]);

  useEffect(() => {
    settingsApi.get().then(res => {
      const data = res.data || {};
      const persona = data.persona || {};
      setAgentName(persona.name || data.agent_name || t('sidebar.default_agent_name'));
    }).catch(() => {});
  }, [currentId]);

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-50 w-60 flex flex-col shrink-0 border-r h-full transform transition-transform duration-200 ease-in-out ${
        open ? 'translate-x-0' : '-translate-x-full'
      } md:static md:inset-auto md:translate-x-0 md:transform-none`}
      style={{
        backgroundColor: 'var(--bg-sidebar)',
        borderColor: 'var(--border)',
      }}
    >
      {/* Mobile close button */}
      {onClose && (
        <div className="flex justify-end p-3 pb-0 md:hidden">
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg"
            style={{ color: 'var(--text-secondary)' }}
            aria-label={t('common.close_menu')}
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Logo */}
      <div
        className="p-5 border-b"
        style={{ borderColor: 'var(--border)' }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center font-bold text-base"
            style={{ backgroundColor: 'var(--brand)', color: '#fff' }}
          >
            AI
          </div>
          <div className="flex-1 min-w-0">
            <h1
              className="text-sm font-bold tracking-wide truncate"
              style={{ color: 'var(--text-primary)' }}
            >
              {t('sidebar.assistant_title')
}
            </h1>

          </div>
        </div>
      </div>

      {/* Assistant Switcher */}
      <AssistantSwitcher />

      {/* 语音全局开关 + 麦克风入口（V-UI-08/09，助理选择器旁） */}
      <div className="px-3 pb-1"><VoiceDock /></div>

      {/* 双模式切换器 (工作|生活) / 家庭版升级卡 */}
      <div className="px-3 pb-1">
        <ModeSwitcher />
      </div>

      {/* W27: 首日 checklist — 建助理 → 聊第一句 → 认识今日信息/仪表盘 */}
      <FirstDayChecklist assistants={assistants} currentId={currentId} mode={mode} />

      {/* Navigation — DRY */}
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto scrollbar-none">
        {/* W15: 试用期"选择方案"置顶高亮 (第一位, dashboard/今日信息之上); 转正后消失 */}
        {plan === 'trial' && <TrialPlanPill trialEndsAt={trialEndsAt} />}
        {navGroups(assistantId, t, { hasDeployment: true, mode, plan })
          // 双模式: 家庭模式隐藏工作专属导航
          .map(g => mode === 'family'
            ? { ...g, items: g.items.filter(it => !FAMILY_HIDDEN.some(p => it.to.includes(p))) }
            : g)
          // Zero-assistant account: assistant-scoped destinations can only
          // render empty states or fail on write — hide them and keep
          // assistant management + system pages reachable.
          .map(g => assistants.length === 0
            ? { ...g, items: g.items.filter(it => !it.to.includes('/assistant/')) }
            : g)
          .filter(g => g.items.length > 0)
          .map(g => (
            <NavGroup key={g.label} label={g.label} items={g.items} />
          ))}
      </nav>

      {/* Theme Toggle + Language + Notifications + Footer */}
      <div
        className="p-3 border-t space-y-1"
        style={{ borderColor: 'var(--border)' }}
      >
        {/* Notification Bell */}
        <div className="flex items-center justify-end px-1">
          <NotificationCenter />
        </div>
        <LanguageToggle />
        <button
          onClick={toggleTheme}
          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-colors hover:bg-[var(--bg-hover)]"
          style={{ color: 'var(--text-secondary)' }}
          title={`Switch to ${theme === 'light' ? t('sidebar.dark') : t('sidebar.light')} mode`}
        >
          <div className="flex items-center gap-2">
            {theme === 'light' ? <Moon size={15} /> : <Sun size={15} />}
            <span>{theme === 'light' ? t('sidebar.dark') : t('sidebar.light')} {t('sidebar.mode')}</span>
          </div>
          <div
            className="w-8 h-4 rounded-full relative transition-colors"
            style={{
              backgroundColor:
                theme === 'dark' ? 'var(--brand)' : 'var(--border)',
            }}
          >
            <div
              className="w-3 h-3 rounded-full absolute top-0.5 transition-transform bg-white"
              style={{
                transform:
                  theme === 'dark' ? 'translateX(17px)' : 'translateX(1px)',
              }}
            />
          </div>
        </button>
        <button
          onClick={() => {
            const tok = getToken();
            if (tok) fetch('/cortex/api/auth/logout', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: tok }) });
            clearToken();
            window.location.href = '/';
          }}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors hover:bg-[var(--bg-hover)]"
          style={{ color: 'var(--text-muted)' }}
          title={t('sidebar.logout')}
        >
          <LogOut size={15} />
          <span>{t('sidebar.logout')}</span>
        </button>
      </div>
    </aside>
  );
}
